/**
 * Settings Context
 * Provides global access to user preferences and organization settings
 * Manages state synchronization between Settings page and other components
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getOrganisationTimesheetSettings, getCachedLiveOrgSettings } from '@/services/settingsRepository';
import { useDataSource } from '@/contexts/DataSourceContext';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';

interface SettingsContextType {
  weekStartDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  setWeekStartDay: (day: 0 | 1 | 2 | 3 | 4 | 5 | 6) => void;
  organizationSettings: OrganisationTimesheetSettings | null;
  refreshSettings: () => Promise<void>;
  isLoading: boolean;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const WEEK_START_DAY_KEY = 'timetrackpro:weekStartDay';

/**
 * Get persisted week start day from localStorage
 */
function getPersistedWeekStartDay(): 0 | 1 | 2 | 3 | 4 | 5 | 6 | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(WEEK_START_DAY_KEY);
    if (stored !== null) {
      const parsed = parseInt(stored, 10);
      if (parsed >= 0 && parsed <= 6) {
        return parsed as 0 | 1 | 2 | 3 | 4 | 5 | 6;
      }
    }
  } catch {
    // Ignore errors
  }
  return null;
}

/**
 * Persist week start day to localStorage
 */
function persistWeekStartDay(day: 0 | 1 | 2 | 3 | 4 | 5 | 6): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(WEEK_START_DAY_KEY, day.toString());
  } catch {
    // Ignore errors
  }
}

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const { getScreenMode } = useDataSource();
  const [isLoading, setIsLoading] = useState(true);
  const [organizationSettings, setOrganizationSettings] = useState<OrganisationTimesheetSettings | null>(null);
  
  // Determine current mode for settings resolution
  const mode = getScreenMode('timesheet');

  // Initialize weekStartDay: in live mode use cached API settings, in demo mode use localStorage
  const [weekStartDay, setWeekStartDayState] = useState<0 | 1 | 2 | 3 | 4 | 5 | 6>(() => {
    if (mode === 'live') {
      // In live mode, API is the source of truth - use cached API settings for initial render
      const cached = getCachedLiveOrgSettings();
      if (cached) {
        return cached.weekStartDay;
      }
    } else {
      // In demo mode, localStorage is the source of truth
      const persisted = getPersistedWeekStartDay();
      if (persisted !== null) {
        return persisted;
      }
    }
    
    // Default to Monday
    return 1;
  });

  const setWeekStartDay = useCallback((day: 0 | 1 | 2 | 3 | 4 | 5 | 6) => {
    setWeekStartDayState(day);
    // Only persist to localStorage in demo mode; in live mode the API is the source of truth
    if (mode !== 'live') {
      persistWeekStartDay(day);
    }
  }, [mode]);

  const refreshSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      // Use 'timesheet' as the screen ID for fetching settings
      const mode = getScreenMode('timesheet');
      const settings = await getOrganisationTimesheetSettings(mode);
      setOrganizationSettings(settings);
      
      if (mode === 'live') {
        // In live mode, always use the API value
        setWeekStartDayState(settings.weekStartDay);
      } else {
        // In demo mode, only update if user hasn't set a local preference
        const persisted = getPersistedWeekStartDay();
        if (persisted === null) {
          setWeekStartDayState(settings.weekStartDay);
        }
      }
    } catch (error) {
      console.error('Failed to refresh settings:', error);
    } finally {
      setIsLoading(false);
    }
  }, [getScreenMode]);

  // Load settings on mount
  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  const value: SettingsContextType = {
    weekStartDay,
    setWeekStartDay,
    organizationSettings,
    refreshSettings,
    isLoading,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
